import { useState, useEffect, useCallback } from 'react';
import { generationLimitsApi, GenerationStatusResponse } from '../services/api';
import { useAuth } from '../context/AuthContext';

// Простой кэш для избежания повторных запросов
const statusCache: Record<string, { data: GenerationStatusResponse; timestamp: number }> = {};
const CACHE_DURATION = 30000; // 30 секунд

export interface UseGenerationStatusResult {
  status: GenerationStatusResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useGenerationStatus(generationType: string): UseGenerationStatusResult {
  const [status, setStatus] = useState<GenerationStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { token } = useAuth();

  const fetchStatus = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    if (!generationType) {
        setLoading(false);
        return;
    }

    // Проверяем кэш
    const cacheKey = `${generationType}_${token}`;
    const cached = statusCache[cacheKey];
    const now = Date.now();
    
    if (cached && (now - cached.timestamp) < CACHE_DURATION) {
      setStatus(cached.data);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const response = await generationLimitsApi.getStatus(generationType);
        
        // Сохраняем в кэш
        statusCache[cacheKey] = {
          data: response.data,
          timestamp: now
        };
        
        setStatus(response.data);
        setLoading(false);
        setError(null);
        return; // Успех, выходим из цикла
      } catch (err: any) {
        console.error(`Ошибка при получении статуса генерации (попытка ${attempt}):`, err);
        if (attempt < 3) {
          setError('Загрузка ваших лимитов...');
          // Пауза перед следующей попыткой
          await new Promise(res => setTimeout(res, 1000 * attempt));
        } else {
          setError(err.response?.data?.error || 'Не удалось загрузить информацию о лимитах генерации');
          setLoading(false);
        }
      }
    }
  }, [generationType, token]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  return {
    status,
    loading,
    error,
    refetch: fetchStatus
  };
}
